import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import {
  Users,
  Landmark,
  CheckCircle2,
  XCircle,
  FileClock,
  RefreshCw,
} from "lucide-react";

import "./AdminReportingPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* =========================================================
   HELPERS
   ========================================================= */

function useAnimatedNumber(targetValue, duration = 750) {
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const target = Number(targetValue) || 0;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return undefined;
    }

    let frame;
    let startedAt = null;

    function animate(timestamp) {
      if (!startedAt) startedAt = timestamp;

      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(target * eased);

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    }

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [targetValue, duration]);

  return value;
}

function AnimatedNumber({ value, locale = "en-GB" }) {
  const animated = useAnimatedNumber(value);

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Math.round(animated));
}

function formatCurrency(value, locale = "en-GB") {
  const numeric = Number(value) || 0;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(numeric);
}

function formatPercent(value) {
  const numeric = Number(value) || 0;
  return `${Math.round(numeric)}%`;
}

function buildLineChart(values, width = 1000, height = 310) {
  const items = Array.isArray(values)
    ? values.map((item) => ({
        ...item,
        value: Number(item.value ?? item.count ?? 0),
        label: item.label || item.month || "",
      }))
    : [];

  if (!items.length) {
    return {
      linePath: "",
      areaPath: "",
      points: [],
      yTicks: [],
      width,
      height,
      plot: {},
    };
  }

  const plot = {
    left: 68,
    right: width - 24,
    top: 22,
    bottom: height - 58,
  };

  const maxValue = Math.max(...items.map((item) => item.value), 1);

  const tickStep = Math.max(1, Math.ceil(maxValue / 4));

  const yMax = tickStep * 4;

  const yTicks = Array.from({ length: 5 }, (_, index) => index * tickStep);

  const points = items.map((item, index) => {
    const x =
      items.length === 1
        ? (plot.left + plot.right) / 2
        : plot.left + (index / (items.length - 1)) * (plot.right - plot.left);

    const normalized = yMax > 0 ? item.value / yMax : 0;

    const y = plot.bottom - normalized * (plot.bottom - plot.top);

    return {
      ...item,
      x,
      y,
      index,
    };
  });

  let linePath = "";

  points.forEach((point, index) => {
    if (index === 0) {
      linePath = `M ${point.x} ${point.y}`;
      return;
    }

    const previous = points[index - 1];

    const controlX = (previous.x + point.x) / 2;

    linePath +=
      ` C ${controlX} ${previous.y},` +
      ` ${controlX} ${point.y},` +
      ` ${point.x} ${point.y}`;
  });

  const first = points[0];
  const last = points[points.length - 1];

  const areaPath =
    `${linePath}` +
    ` L ${last.x} ${plot.bottom}` +
    ` L ${first.x} ${plot.bottom} Z`;

  return {
    linePath,
    areaPath,
    points,
    yTicks,
    yMax,
    width,
    height,
    plot,
  };
}

/* =========================================================
   DONUT
   ========================================================= */

function DonutChart({ items, centerLabel = "" }) {
  const total = items.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  let offset = 0;

  const segments = items.map((item) => {
    const value = Number(item.value) || 0;
    const percentage = total > 0 ? (value / total) * 100 : 0;

    const segment = {
      ...item,
      percentage,
      offset,
    };

    offset += percentage;
    return segment;
  });

  return (
    <div className="admin-reporting-donut-layout">
      <div className="admin-reporting-donut-wrap">
        <svg
          className="admin-reporting-donut"
          viewBox="0 0 120 120"
          role="img"
          aria-label={centerLabel}
        >
          <circle
            className="admin-reporting-donut__track"
            cx="60"
            cy="60"
            r="42"
            pathLength="100"
          />

          {segments.map((segment, index) => (
            <circle
              key={segment.key}
              className={`admin-reporting-donut__segment admin-reporting-donut__segment--${index + 1}`}
              cx="60"
              cy="60"
              r="42"
              pathLength="100"
              style={{
                "--segment-size": `${segment.percentage}`,
                "--segment-offset": `${-segment.offset}`,
              }}
            />
          ))}
        </svg>
      </div>

      <div className="admin-reporting-donut-legend">
        {segments.map((segment, index) => (
          <div className="admin-reporting-donut-legend__item" key={segment.key}>
            <span
              className={`admin-reporting-donut-legend__dot admin-reporting-donut-legend__dot--${index + 1}`}
            />

            <span className="admin-reporting-donut-legend__label">
              {segment.label}
            </span>

            <strong>{formatPercent(segment.percentage)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   LINE CHART
   ========================================================= */

function AnimatedLineChart({
  values = [],
  ariaLabel = "",
  valueLabel = "Users",
  tooltipLabel = "Total users",
}) {
  const [activeIndex, setActiveIndex] = React.useState(null);

  const chart = React.useMemo(() => buildLineChart(values), [values]);

  if (!chart.linePath) {
    return <div className="admin-reporting-chart-empty">—</div>;
  }

  const activePoint = activeIndex !== null ? chart.points[activeIndex] : null;

  return (
    <div
      className="admin-reporting-line-chart"
      onMouseLeave={() => setActiveIndex(null)}
    >
      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        role="img"
        aria-label={ariaLabel}
      >
        <defs>
          <linearGradient
            id="admin-reporting-area-gradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#a9c58d" stopOpacity="0.34" />

            <stop offset="100%" stopColor="#a9c58d" stopOpacity="0.08" />
          </linearGradient>
        </defs>

        {/* Y GRID + VALUES */}

        {chart.yTicks.map((tick) => {
          const y =
            chart.plot.bottom -
            (tick / chart.yMax) * (chart.plot.bottom - chart.plot.top);

          return (
            <g key={tick}>
              <line
                className="admin-reporting-line-chart__grid"
                x1={chart.plot.left}
                x2={chart.plot.right}
                y1={y}
                y2={y}
              />

              <text
                className="admin-reporting-line-chart__y-tick"
                x={chart.plot.left - 15}
                y={y + 4}
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* Y AXIS LABEL */}

        <text
          className="admin-reporting-line-chart__axis-title"
          x="18"
          y={(chart.plot.top + chart.plot.bottom) / 2}
          textAnchor="middle"
          transform={`rotate(-90 18 ${
            (chart.plot.top + chart.plot.bottom) / 2
          })`}
        >
          {valueLabel}
        </text>

        {/* AREA */}

        <path className="admin-reporting-line-chart__area" d={chart.areaPath} />

        {/* LINE */}

        <path className="admin-reporting-line-chart__line" d={chart.linePath} />

        {/* ACTIVE VERTICAL GUIDE */}

        {activePoint ? (
          <line
            className="admin-reporting-line-chart__guide"
            x1={activePoint.x}
            x2={activePoint.x}
            y1={chart.plot.top}
            y2={chart.plot.bottom}
          />
        ) : null}

        {/* X LABELS + POINTS */}

        {chart.points.map((point, index) => (
          <g key={point.month || index}>
            <text
              className="admin-reporting-line-chart__x-tick"
              x={point.x}
              y={chart.plot.bottom + 27}
              textAnchor="middle"
            >
              {point.label}
            </text>

            <circle
              className={`admin-reporting-line-chart__point ${
                activeIndex === index ? "is-active" : ""
              }`}
              cx={point.x}
              cy={point.y}
              r="5"
            />

            <circle
              className="admin-reporting-line-chart__hit-area"
              cx={point.x}
              cy={point.y}
              r="18"
              tabIndex="0"
              role="button"
              aria-label={`${point.label}: ${point.value} ${tooltipLabel}`}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
              onClick={() => setActiveIndex(index)}
            />
          </g>
        ))}
      </svg>

      {activePoint ? (
        <div
          className="admin-reporting-line-chart__tooltip"
          style={{
            left: `${(activePoint.x / chart.width) * 100}%`,
            top: `${(activePoint.y / chart.height) * 100}%`,
          }}
        >
          <span>{activePoint.label}</span>

          <strong>{activePoint.value}</strong>

          <small>{tooltipLabel}</small>
        </div>
      ) : null}
    </div>
  );
}

/* =========================================================
   ANIMATED BAR
   ========================================================= */

function AnimatedBar({ label, value, maxValue, suffix = "" }) {
  const percentage =
    maxValue > 0 ? Math.min((Number(value) / maxValue) * 100, 100) : 0;

  return (
    <div className="admin-reporting-bar-row">
      <span className="admin-reporting-bar-row__label">{label}</span>

      <div className="admin-reporting-bar-row__track">
        <span
          className="admin-reporting-bar-row__fill"
          style={{ "--bar-width": `${percentage}%` }}
        />
      </div>

      <span className="admin-reporting-bar-row__value">
        {value}
        {suffix}
      </span>
    </div>
  );
}

/* =========================================================
   STAT CARD
   ========================================================= */

function StatCard({ title, subtitle, value, locale, delay = 0 }) {
  return (
    <article
      className="admin-reporting-stat"
      style={{ "--card-delay": `${delay}ms` }}
    >
      <h3>{title}</h3>
      <p>{subtitle}</p>

      <strong>
        <AnimatedNumber value={value} locale={locale} />
      </strong>
    </article>
  );
}

/* =========================================================
   PAGE
   ========================================================= */

export default function AdminReportingPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { t, i18n } = useTranslation("admin-reporting");

  const [activeTab, setActiveTab] = React.useState("USERS");
  const [reporting, setReporting] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const locale =
    i18n.language === "nl"
      ? "nl-BE"
      : i18n.language === "es"
        ? "es-ES"
        : i18n.language === "ca"
          ? "ca-ES"
          : "en-GB";

  const loadReporting = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const accessToken = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE}/api/admin/reporting/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t("errors.load", "Could not load reporting information."),
        );
      }

      setReporting(data);
    } catch (loadError) {
      console.error("Failed to load admin reporting:", loadError);

      setError(
        loadError.message ||
          t("errors.load", "Could not load reporting information."),
      );
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, t]);

  React.useEffect(() => {
    loadReporting();
  }, [loadReporting]);

  const users = reporting?.users || {};
  const loans = reporting?.loan_cases || {};

  const userCategories = [
    {
      key: "HOMEOWNER",
      label: t("users.categories.homebuyers", "Homebuyers"),
      value: Number(users?.categories?.homeowners || 0),
      description: t(
        "users.categoryDescriptions.homebuyers",
        "Registered homebuyer accounts",
      ),
    },
    {
      key: "LENDER",
      label: t("users.categories.lenders", "Lender users"),
      value: Number(users?.categories?.lenders || 0),
      description: t(
        "users.categoryDescriptions.lenders",
        "Registered lender organisation users",
      ),
    },
    {
      key: "OSS",
      label: t("users.categories.oss", "OSS users"),
      value: Number(users?.categories?.oss || 0),
      description: t(
        "users.categoryDescriptions.oss",
        "Registered One-Stop Shop users",
      ),
    },
    {
      key: "PLATFORM_ADMIN",
      label: t("users.categories.admins", "Admins / support"),
      value: Number(users?.categories?.admins || 0),
      description: t(
        "users.categoryDescriptions.admins",
        "Platform administration accounts",
      ),
    },
  ];

  if (Number(users?.categories?.investors || 0) > 0) {
    userCategories.push({
      key: "INVESTOR",
      label: t("users.categories.investors", "Investors"),
      value: Number(users.categories.investors),
      description: t(
        "users.categoryDescriptions.investors",
        "Registered investor accounts",
      ),
    });
  }

  const userDistributionMax = Math.max(
    ...userCategories.map((item) => item.value),
    1,
  );

  const regionalUsers = Array.isArray(users?.regions)
    ? users.regions.map((region) => ({
        key: region.id ?? region.code,

        label: region.name || region.code || "—",

        code: region.code || "",

        countryCode: region.country_code || "",

        value: Number(region.user_count || 0),
      }))
    : [];

  const regionalUsersMax = Math.max(
    ...regionalUsers.map((item) => item.value),
    1,
  );

  const loanStatusItems = [
    {
      key: "FINALISED",
      label: t("loans.status.finalised", "Finalised"),
      value: Number(loans?.finalised_records || 0),
    },
    {
      key: "DRAFT",
      label: t("loans.status.draft", "Draft"),
      value: Number(loans?.draft_records || 0),
    },
    {
      key: "REJECTED",
      label: t("loans.status.rejected", "Rejected"),
      value: Number(loans?.rejected_records || 0),
    },
  ];

  const rejectionReasons = Array.isArray(loans?.rejection_reasons)
    ? loans.rejection_reasons
    : [];

  const rejectionMax = Math.max(
    ...rejectionReasons.map((item) =>
      Number(item.percentage || item.value || 0),
    ),
    1,
  );

  if (loading) {
    return (
      <div className="admin-reporting">
        <div className="admin-reporting-loading">
          <div className="admin-reporting-loading__header" />
          <div className="admin-reporting-loading__tabs" />
          <div className="admin-reporting-loading__grid">
            {[1, 2, 3, 4].map((item) => (
              <div className="admin-reporting-loading__card" key={item} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-reporting">
        <div className="admin-reporting-error">
          <strong>{t("errors.title", "Reporting unavailable")}</strong>
          <p>{error}</p>

          <button type="button" onClick={loadReporting}>
            <RefreshCw size={16} />
            {t("actions.retry", "Try again")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-reporting">
      {/* HEADER */}

      <header className="admin-reporting__header">
        <div>
          <h1>{t("header.title", "Reporting")}</h1>

          <p>
            {t(
              "header.subtitle",
              "Review platform statistics for users and loan cases. Detailed tables and animated charts provide a quick overview for monitoring and reporting.",
            )}
          </p>
        </div>

        <span className="admin-reporting__badge">
          {t("header.badge", "Admin reporting")}
        </span>
      </header>

      {/* TABS */}

      <div className="admin-reporting-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "USERS"}
          className={`admin-reporting-tab ${
            activeTab === "USERS" ? "is-active" : ""
          }`}
          onClick={() => setActiveTab("USERS")}
        >
          <span className="admin-reporting-tab__icon">
            <Users size={20} />
          </span>

          <span className="admin-reporting-tab__copy">
            <strong>{t("tabs.users.title", "Users Reporting")}</strong>
            <span>
              {t(
                "tabs.users.subtitle",
                "Roles, regions, activity and account status",
              )}
            </span>
          </span>

          <span className="admin-reporting-tab__count">
            {users?.total_users || 0} {t("common.users", "users")}
          </span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "LOANS"}
          className={`admin-reporting-tab ${
            activeTab === "LOANS" ? "is-active" : ""
          }`}
          onClick={() => setActiveTab("LOANS")}
        >
          <span className="admin-reporting-tab__icon">
            <Landmark size={20} />
          </span>

          <span className="admin-reporting-tab__copy">
            <strong>{t("tabs.loans.title", "Loan Cases Reporting")}</strong>
            <span>
              {t(
                "tabs.loans.subtitle",
                "Draft, finalised and rejected lender-entered records",
              )}
            </span>
          </span>

          <span className="admin-reporting-tab__count">
            {loans?.total_records || 0} {t("common.cases", "cases")}
          </span>
        </button>
      </div>

      {/* USERS */}

      {activeTab === "USERS" ? (
        <div className="admin-reporting-section" key="users-reporting">
          <section className="admin-reporting-stats-grid">
            <StatCard
              title={t("users.stats.total.title", "Total users")}
              subtitle={t(
                "users.stats.total.subtitle",
                "All registered platform accounts",
              )}
              value={users?.total_users || 0}
              locale={locale}
              delay={0}
            />

            <StatCard
              title={t("users.stats.homebuyers.title", "Homebuyers")}
              subtitle={t(
                "users.stats.homebuyers.subtitle",
                "Registered HB accounts",
              )}
              value={users?.categories?.homeowners || 0}
              locale={locale}
              delay={60}
            />

            <StatCard
              title={t("users.stats.lenders.title", "Lender users")}
              subtitle={t("users.stats.lenders.subtitle", "Bank users")}
              value={users?.categories?.lenders || 0}
              locale={locale}
              delay={120}
            />

            <StatCard
              title={t("users.stats.oss.title", "OSS users")}
              subtitle={t("users.stats.oss.subtitle", "One-Stop Shop users")}
              value={users?.categories?.oss || 0}
              locale={locale}
              delay={180}
            />
          </section>

          <section className="admin-reporting-two-column">
            <article className="admin-reporting-panel">
              <div className="admin-reporting-panel__heading">
                <h2>{t("users.distribution.title", "User Distribution")}</h2>
                <p>
                  {t("users.distribution.subtitle", "Users by platform role")}
                </p>
              </div>

              <div className="admin-reporting-inner-card">
                <h3>
                  {t(
                    "users.distribution.cardTitle",
                    "Registered users by category",
                  )}
                </h3>

                <div className="admin-reporting-bars">
                  {userCategories.map((item) => (
                    <AnimatedBar
                      key={item.key}
                      label={item.label}
                      value={item.value}
                      maxValue={userDistributionMax}
                    />
                  ))}
                </div>
              </div>
            </article>

            <article className="admin-reporting-panel">
              <div className="admin-reporting-panel__heading">
                <h2>{t("users.regional.title", "Users by Market Region")}</h2>

                <p>
                  {t(
                    "users.regional.subtitle",
                    "Users associated with each active LEG-UP market",
                  )}
                </p>
              </div>

              <div className="admin-reporting-inner-card">
                <h3>{t("users.regional.cardTitle", "Market participation")}</h3>

                {regionalUsers.length > 0 ? (
                  <>
                    <div className="admin-reporting-bars">
                      {regionalUsers.map((item) => (
                        <AnimatedBar
                          key={item.key}
                          label={item.label}
                          value={item.value}
                          maxValue={regionalUsersMax}
                        />
                      ))}
                    </div>

                    <p className="admin-reporting-region-note">
                      {t(
                        "users.regional.note",
                        "Users may be associated with more than one market region, so regional counts are not mutually exclusive.",
                      )}
                    </p>
                  </>
                ) : (
                  <div className="admin-reporting-chart-empty">
                    {t(
                      "users.regional.empty",
                      "No regional user data is available yet.",
                    )}
                  </div>
                )}
              </div>
            </article>
          </section>

          <article className="admin-reporting-panel admin-reporting-panel--wide">
            <div className="admin-reporting-panel__heading">
              <h2>{t("users.growth.title", "User Growth")}</h2>
              <p>{t("users.growth.subtitle", "Monthly registered users")}</p>
            </div>

            <div className="admin-reporting-inner-card admin-reporting-inner-card--line">
              <AnimatedLineChart
                values={users?.monthly_growth || []}
                ariaLabel={t("users.growth.chartLabel", "Monthly user growth")}
                valueLabel={t("users.growth.yAxis", "Users")}
                tooltipLabel={t("users.growth.tooltip", "Total users")}
              />
            </div>
          </article>

          <article className="admin-reporting-panel admin-reporting-panel--wide">
            <div className="admin-reporting-panel__heading">
              <h2>{t("users.table.title", "Users Table")}</h2>

              <p>
                {t("users.table.subtitle", "Separate table for user reporting")}
              </p>
            </div>

            <div className="admin-reporting-table-wrap">
              <table className="admin-reporting-table">
                <thead>
                  <tr>
                    <th>{t("users.table.category", "Category")}</th>
                    <th>{t("users.table.total", "Total")}</th>
                    <th>{t("users.table.share", "Share")}</th>
                    <th>{t("users.table.meaning", "Meaning")}</th>
                  </tr>
                </thead>

                <tbody>
                  {userCategories.map((item) => {
                    const share =
                      Number(users?.total_users || 0) > 0
                        ? (item.value / Number(users.total_users)) * 100
                        : 0;

                    return (
                      <tr key={item.key}>
                        <td>
                          <strong>{item.label}</strong>
                        </td>

                        <td>{item.value}</td>

                        <td>{formatPercent(share)}</td>

                        <td>{item.description}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </article>
        </div>
      ) : null}

      {/* LOANS */}

      {activeTab === "LOANS" ? (
        <div className="admin-reporting-section" key="loan-reporting">
          <section className="admin-reporting-stats-grid">
            <StatCard
              title={t("loans.stats.total.title", "Total loan records")}
              subtitle={t(
                "loans.stats.total.subtitle",
                "Lender-entered case records",
              )}
              value={loans?.total_records || 0}
              locale={locale}
              delay={0}
            />

            <StatCard
              title={t("loans.stats.finalised.title", "Finalised records")}
              subtitle={t(
                "loans.stats.finalised.subtitle",
                "Completed lender records",
              )}
              value={loans?.finalised_records || 0}
              locale={locale}
              delay={60}
            />

            <StatCard
              title={t("loans.stats.rejected.title", "Rejected records")}
              subtitle={t(
                "loans.stats.rejected.subtitle",
                "Rejected outside LEG-UP",
              )}
              value={loans?.rejected_records || 0}
              locale={locale}
              delay={120}
            />

            <StatCard
              title={t("loans.stats.draft.title", "Draft records")}
              subtitle={t(
                "loans.stats.draft.subtitle",
                "Incomplete lender entries",
              )}
              value={loans?.draft_records || 0}
              locale={locale}
              delay={180}
            />
          </section>

          <section className="admin-reporting-two-column">
            <article className="admin-reporting-panel">
              <div className="admin-reporting-panel__heading">
                <h2>{t("loans.status.title", "Loan Record Status")}</h2>
                <p>
                  {t(
                    "loans.status.subtitle",
                    "Simplified case record categories",
                  )}
                </p>
              </div>

              <div className="admin-reporting-inner-card admin-reporting-inner-card--donut">
                <h3>
                  {t(
                    "loans.status.cardTitle",
                    "Draft, finalised and rejected records",
                  )}
                </h3>

                <DonutChart
                  items={loanStatusItems}
                  centerLabel={t(
                    "loans.status.chartLabel",
                    "Loan case status distribution",
                  )}
                />
              </div>
            </article>

            <article className="admin-reporting-panel">
              <div className="admin-reporting-panel__heading">
                <h2>
                  {t("loans.rejectionReasons.title", "Rejected Case Reasons")}
                </h2>

                <p>
                  {t(
                    "loans.rejectionReasons.subtitle",
                    "Reasons entered by lender or offline process",
                  )}
                </p>
              </div>

              <div className="admin-reporting-inner-card">
                <h3>
                  {t(
                    "loans.rejectionReasons.cardTitle",
                    "Main reporting reasons",
                  )}
                </h3>

                <div className="admin-reporting-bars admin-reporting-bars--reasons">
                  {rejectionReasons.length > 0 ? (
                    rejectionReasons.map((reason) => {
                      const value = Number(
                        reason.percentage ?? reason.value ?? 0,
                      );

                      return (
                        <AnimatedBar
                          key={reason.key || reason.label}
                          label={t(
                            `loans.rejectionReasons.items.${reason.key}`,
                            reason.label || reason.key,
                          )}
                          value={value}
                          maxValue={rejectionMax}
                          suffix="%"
                        />
                      );
                    })
                  ) : (
                    <div className="admin-reporting-chart-empty">
                      {t(
                        "loans.rejectionReasons.empty",
                        "No rejection reason data is available yet.",
                      )}
                    </div>
                  )}
                </div>
              </div>
            </article>
          </section>

          <article className="admin-reporting-panel admin-reporting-panel--wide">
            <div className="admin-reporting-panel__heading">
              <h2>{t("loans.volume.title", "Loan Record Volume")}</h2>

              <p>
                {t(
                  "loans.volume.subtitle",
                  "Monthly count of saved loan case records",
                )}
              </p>
            </div>

            <div className="admin-reporting-inner-card admin-reporting-inner-card--line">
              <AnimatedLineChart
                values={loans?.monthly_volume || []}
                ariaLabel={t(
                  "loans.volume.chartLabel",
                  "Monthly loan record volume",
                )}
                valueLabel={t("loans.volume.yAxis", "Loan records")}
                tooltipLabel={t("loans.volume.tooltip", "Loan records")}
              />
            </div>
          </article>

          <article className="admin-reporting-panel admin-reporting-panel--wide">
            <div className="admin-reporting-panel__heading">
              <h2>{t("loans.table.title", "Loan Records Table")}</h2>

              <p>
                {t(
                  "loans.table.subtitle",
                  "Simplified reporting table based on lender-entered records",
                )}
              </p>
            </div>

            <div className="admin-reporting-table-wrap">
              <table className="admin-reporting-table admin-reporting-table--loans">
                <thead>
                  <tr>
                    <th>{t("loans.table.category", "Record category")}</th>
                    <th>{t("loans.table.records", "Records")}</th>
                    <th>
                      {t("loans.table.totalAmount", "Total registered amount")}
                    </th>
                    <th>
                      {t(
                        "loans.table.averageAmount",
                        "Average registered amount",
                      )}
                    </th>
                    <th>{t("loans.table.share", "Share")}</th>
                    <th>{t("loans.table.meaning", "Meaning")}</th>
                  </tr>
                </thead>

                <tbody>
                  {(loans?.table || []).map((row) => (
                    <tr key={row.key}>
                      <td>
                        <span
                          className={`admin-reporting-status admin-reporting-status--${String(
                            row.key,
                          ).toLowerCase()}`}
                        >
                          {t(
                            `loans.status.${String(row.key).toLowerCase()}`,
                            row.label,
                          )}
                        </span>
                      </td>

                      <td>{row.records ?? 0}</td>

                      <td>
                        {row.total_amount != null
                          ? formatCurrency(row.total_amount, locale)
                          : "—"}
                      </td>

                      <td>
                        {row.average_amount != null
                          ? formatCurrency(row.average_amount, locale)
                          : "—"}
                      </td>

                      <td>{formatPercent(row.share || 0)}</td>

                      <td>
                        {t(
                          `loans.table.meanings.${String(row.key).toLowerCase()}`,
                          row.meaning || "",
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        </div>
      ) : null}
    </div>
  );
}
